import { combineReducers } from 'redux';
import { call, put, select, takeEvery } from 'redux-saga/effects';

import { api } from './api';
import { environment } from 'environments/environment';
import { ChargeGroupDTO, SummaryViewVO } from 'generated/bslEbill/summaryView/models';
import { ExtendedAxiosResponse } from 'helpers/apiClient';
import {
  AppAction,
  createActionType,
  createApiActionCreators,
  createReducer,
  RequestActionTypes,
} from 'helpers/redux/reduxHelpers';
import { AppState } from 'helpers/store/models/AppState';
import { ApiClientRequest } from 'models/ApiClientRequest';
import { GetServiceSummayViewPayload } from 'models/Billing';
import { getApiUrl, performApiCall } from 'saga/apiClient/ducks';
import { getKahlActions } from 'saga/kahl/ducks';

/* STATE */
export interface SummaryViewState {
  data: SummaryViewVO;
  dataList: SummaryViewVO[];
}

/* ACTION TYPES */
export enum SummaryViewActionTypes {
  GetData = '@@SummaryView/GET_DATA',
  GetDataList = '@@SummaryView/GET_DATA_LIST',
}

/* ACTIONS */
export const getDataAction = createApiActionCreators(SummaryViewActionTypes.GetData);
export const getDataListAction = createApiActionCreators(SummaryViewActionTypes.GetDataList);

/* REDUCERS */
const initialState: SummaryViewState = {
  data: {},
  dataList: [],
};

const data = createReducer(initialState.data, {
  [SummaryViewActionTypes.GetData]: {
    [RequestActionTypes.SUCCESS]: (state: SummaryViewVO, payload: SummaryViewVO) => payload,
    [RequestActionTypes.FAILURE]: () => {
      undefined;
    },
  },
});

const dataList = createReducer(initialState.dataList, {
  [SummaryViewActionTypes.GetData]: {
    [RequestActionTypes.SUCCESS]: (state: Array<SummaryViewVO>, payload: SummaryViewVO) => {
      if (state.some((e) => e.serviceInfo?.serviceId === payload.serviceInfo?.serviceId)) {
        return [...state];
      } else {
        return [...state, payload];
      }
    },
    [RequestActionTypes.FAILURE]: () => [],
  },
});

export default combineReducers<SummaryViewState>({
  data,
  dataList,
});

/* SELECTORS */
const selectSummaryView = (state: AppState) => state.summaryView;

export const selectSummaryViewData = (state: AppState): SummaryViewVO => selectSummaryView(state).data;

export const selectSummaryViewDataList = (state: AppState): SummaryViewVO[] => selectSummaryView(state).dataList;

export const selectInvoiceNumber = (state: AppState): string | undefined =>
  selectSummaryViewData(state).invoiceInfo?.invoiceNo;

export const selectServiceId = (state: AppState): number | undefined =>
  selectSummaryViewData(state).serviceInfo?.serviceId;

export const selectServiceTaxableAmount = (state: AppState): number | undefined =>
  selectSummaryViewData(state).serviceInfo?.taxableAmount;

export const selectServiceTotalAmount = (state: AppState): number | undefined =>
  selectSummaryViewData(state).serviceInfo?.totalAmount;

export const selectChargeGroups = (state: AppState): Array<ChargeGroupDTO> =>
  selectSummaryViewData(state).chargeGroups || [];

/* SAGAS */
function* getSummaryViewData({ payload }: AppAction<GetServiceSummayViewPayload>) {
  const dataList: SummaryViewVO[] = yield select(selectSummaryViewDataList);

  if (dataList.some((s) => String(s.serviceInfo?.serviceId) === String(payload.serviceId))) {
    return;
  }

  const request: ApiClientRequest = {
    apiCall: call(
      api.getData,
      yield getApiUrl(environment.urlConfig.billingManagement),
      payload.invoiceNumber,
      payload.serviceNo,
      payload.serviceId
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(getDataAction.success(resp.data));
  } else {
    yield put(getDataAction.failure());
    yield put(getKahlActions.request({ infoMessageIds: 'ID0013' }));
  }
}

/* EXPORT */
export function* summaryViewSaga() {
  yield takeEvery(createActionType(SummaryViewActionTypes.GetData, RequestActionTypes.REQUEST), getSummaryViewData);
}
